import React, { FC, memo, useEffect, useMemo } from 'react'
import NonFlexingContainer from '@sport1/news-styleguide/NonFlexingContainer'
import DropDown from '@sport1/news-styleguide/DropDown'
import { DropDownOption } from '@sport1/news-styleguide/Types'
import CardHeadline from '@sport1/news-styleguide/CardHeadline'
import { useLayoutValueContext } from '@/context/LayoutValueContext'
import { useTeamContext } from '@/context/TeamContext'
import { useMediaQueryContext } from '@/utils/breakpoints/MediaQuery'
import { SizeTypes } from '@/types/size'
import useDropdownTracking from '@/hooks/useDropdownTracking'

export type TeamStatisticsDropdownKeys = 'competitions'
export type TeamStatisticsDropdownValueType = DropDownOption<string>

type Props = {
    componentKey: string
}

const CompetitionDropDown: FC<Props> = ({ componentKey }) => {
    const { team } = useTeamContext()
    const trackDropdownChange = useDropdownTracking()
    const { selectedValue, values, selectValue } = useLayoutValueContext<
        TeamStatisticsDropdownKeys,
        TeamStatisticsDropdownValueType
    >()
    const { currentDevice } = useMediaQueryContext()
    const headline = useMemo(
        () =>
            currentDevice === SizeTypes.MOBILE
                ? `${team.shortName} Statistik`
                : `${team.name} Statistik`,
        [currentDevice, team.name, team.shortName]
    )

    useEffect(() => {
        if (values?.competitions?.[0]) {
            selectValue('competitions', values.competitions[0])
        }
    }, [selectValue, values?.competitions])

    const onOptionChange = React.useCallback(
        (option: DropDownOption<string>) => {
            selectValue('competitions', option)
            trackDropdownChange()
        },
        [selectValue, trackDropdownChange]
    )

    return (
        <NonFlexingContainer
            testID={`${componentKey}-competition-dropdown`}
            backgroundColor="pearl"
            marginBottom={['spacing-7', 'spacing-7', 'spacing-9', 'spacing-10']}
        >
            <NonFlexingContainer
                testID={`${componentKey}-wrapper`}
                paddingX={['spacing-7', 'spacing-7', 'spacing-9', 'spacing-10']}
                paddingY={['spacing-7', 'spacing-7', 'spacing-9']}
            >
                <CardHeadline
                    testID={`${componentKey}-headline`}
                    cardHeadlineType={['cardHeadlineS', 'cardHeadlineM', 'cardHeadlineM']}
                    text={headline}
                />
                {values?.competitions?.length ? (
                    <NonFlexingContainer
                        testID={`${componentKey}-dropdown-wrapper`}
                        width={['100%', '100%', '50%', '30%']}
                        marginTop="spacing-7"
                    >
                        <DropDown
                            testID={`${componentKey}-dropdown`}
                            options={values.competitions}
                            selectedOption={selectedValue?.competitions}
                            onOptionChange={onOptionChange}
                        />
                    </NonFlexingContainer>
                ) : null}
            </NonFlexingContainer>
        </NonFlexingContainer>
    )
}

export default memo(CompetitionDropDown)
